import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { userService } from '../services/userService';
import { formatDate } from '../utils/formatters';
import { 
  Users, 
  GraduationCap, 
  User, 
  Search, 
  Filter,
  Ban,
  Unlock,
  Eye,
  Loader,
  AlertCircle,
  Shield,
  ShieldOff,
  MoreVertical,
  Mail,
  Phone,
  Calendar,
  Star,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  X,
  Check,
  AlertTriangle,
  Send,
  FileText,
  MessageSquare
} from 'lucide-react';

// Modern Popup Notification Component
const NotificationPopup = ({ type, title, message, onClose, duration = 5000 }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getNotificationConfig = () => {
    const configs = {
      success: {
        bg: 'bg-emerald-50 border-emerald-200',
        icon: 'text-emerald-600 bg-emerald-100',
        iconComponent: CheckCircle,
        title: 'text-emerald-800',
        message: 'text-emerald-700'
      },
      error: {
        bg: 'bg-red-50 border-red-200',
        icon: 'text-red-600 bg-red-100',
        iconComponent: XCircle,
        title: 'text-red-800',
        message: 'text-red-700'
      },
      warning: {
        bg: 'bg-amber-50 border-amber-200',
        icon: 'text-amber-600 bg-amber-100',
        iconComponent: AlertTriangle,
        title: 'text-amber-800',
        message: 'text-amber-700'
      },
      info: {
        bg: 'bg-blue-50 border-blue-200',
        icon: 'text-blue-600 bg-blue-100',
        iconComponent: Shield,
        title: 'text-blue-800',
        message: 'text-blue-700'
      }
    };
    return configs[type] || configs.info;
  };

  const config = getNotificationConfig();
  const IconComponent = config.iconComponent;

  return (
    <div className={`
      fixed top-6 right-6 z-50 max-w-sm w-full transform transition-all duration-300
      ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
    `}>
      <div className={`rounded-xl border p-4 shadow-lg ${config.bg}`}>
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${config.icon}`}>
            <IconComponent className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold text-sm ${config.title}`}>{title}</h4>
            <p className={`text-sm mt-1 ${config.message}`}>{message}</p>
          </div>
          <button
            onClick={() => {
              setIsVisible(false);
              setTimeout(onClose, 300);
            }}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Email Modal Component
const EmailModal = ({ user, isOpen, onClose, onSend }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;

    setLoading(true);
    await onSend(user.id, { subject, message });
    setLoading(false);
    
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Send Email to {user?.name}</h2>
              <p className="text-gray-600 text-sm mt-1">Email will be sent to: {user?.email}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                rows="8"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 resize-none"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Email Guidelines</p>
                  <ul className="text-xs text-blue-700 space-y-1 mt-1">
                    <li>• Keep messages professional and clear</li>
                    <li>• Include all necessary details</li>
                    <li>• Avoid sensitive information in email body</li>
                    <li>• Use appropriate greeting and closing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              type="submit"
              disabled={loading || !subject.trim() || !message.trim()}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
            >
              {loading ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span>{loading ? 'Sending...' : 'Send Email'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors duration-200 font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserCard = ({ user, onBan, onUnban, onSendEmail, loading, theme = 'default' }) => {
  const [showBanForm, setShowBanForm] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleBan = () => {
    if (!banReason.trim()) {
      return;
    }
    onBan(user.id, banReason);
    setShowBanForm(false);
    setBanReason('');
  };

  const isBanned = user.status === 'banned';

  // Theme configurations for different user types
  const themeConfigs = {
    default: {
      primary: 'blue',
      gradient: 'from-blue-50 to-blue-100',
      border: 'border-blue-200',
      accent: 'blue',
      badge: 'blue'
    },
    student: {
      primary: 'emerald',
      gradient: 'from-emerald-50 to-emerald-100',
      border: 'border-emerald-200',
      accent: 'emerald',
      badge: 'emerald'
    },
    tutor: {
      primary: 'purple',
      gradient: 'from-purple-50 to-purple-100',
      border: 'border-purple-200',
      accent: 'purple',
      badge: 'purple'
    },
    pending: {
      primary: 'amber',
      gradient: 'from-amber-50 to-amber-100',
      border: 'border-amber-200',
      accent: 'amber',
      badge: 'amber'
    },
    banned: {
      primary: 'red',
      gradient: 'from-red-50 to-red-100',
      border: 'border-red-200',
      accent: 'red',
      badge: 'red'
    }
  };

  const config = themeConfigs[theme] || themeConfigs.default;

  const getStatusBadge = () => {
    if (isBanned) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
          <Ban className="h-3 w-3 mr-1" />
          Banned
        </span>
      );
    }

    if (user.role === 'tutor') {
      if (user.verification_status === 'approved') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
            <Shield className="h-3 w-3 mr-1" />
            Verified Tutor
          </span>
        );
      } else if (user.verification_status === 'pending') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending Approval
          </span>
        );
      } else if (user.verification_status === 'rejected') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      }
    }

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${config.accent}-100 text-${config.accent}-800 border border-${config.accent}-200`}>
        <CheckCircle className="h-3 w-3 mr-1" />
        Active
      </span>
    );
  };

  return (
    <>
      <div className={`bg-gradient-to-br ${config.gradient} rounded-xl border ${config.border} p-6 hover:shadow-lg transition-all duration-300 group`}>
        <div className="flex items-start justify-between">
          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-start space-x-4">
              {/* Avatar */}
              <div className={`p-3 rounded-2xl bg-${config.accent}-100 border border-${config.accent}-200`}>
                {user.role === 'tutor' ? (
                  <GraduationCap className={`h-6 w-6 text-${config.accent}-600`} />
                ) : (
                  <User className={`h-6 w-6 text-${config.accent}-600`} />
                )}
              </div>
              
              {/* User Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{user.name}</h3>
                  {getStatusBadge()}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    {user.phone && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone className="h-4 w-4" />
                        <span>{user.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Joined {formatDate(user.created_at)}</span>
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${config.accent}-100 text-${config.accent}-700 capitalize`}>
                      {user.role}
                    </div>
                  </div>
                </div>

                {/* Tutor Specific Stats */}
                {user.role === 'tutor' && (user.rating || user.trust_score) && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {user.rating && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Rating:</span>
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            <span className="font-semibold text-gray-900">{user.rating}/5.0</span>
                          </div>
                        </div>
                      )}
                      {user.trust_score && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Trust Score:</span>
                          <span className="font-semibold text-blue-600">
                            {Math.round(user.trust_score * 100)}%
                          </span>
                        </div>
                      )}
                      {user.completed_assignments && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Completed:</span>
                          <span className="font-semibold text-green-600">
                            {user.completed_assignments}
                          </span>
                        </div>
                      )}
                      {user.response_rate && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Response Rate:</span>
                          <span className="font-semibold text-purple-600">
                            {user.response_rate}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white transition-colors duration-200"
            >
              <MoreVertical className="h-5 w-5" />
            </button>

            {showActions && (
              <div className="absolute right-0 top-10 w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-10 py-2">
                <button
                  onClick={() => {/* View profile logic */}}
                  className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <Eye className="h-4 w-4" />
                  <span>View Profile</span>
                </button>
                
                {!isBanned ? (
                  <button
                    onClick={() => setShowBanForm(true)}
                    disabled={loading}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200"
                  >
                    <Ban className="h-4 w-4" />
                    <span>Ban User</span>
                  </button>
                ) : (
                  <button
                    onClick={() => onUnban(user.id)}
                    disabled={loading}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50 disabled:opacity-50 transition-colors duration-200"
                  >
                    <Unlock className="h-4 w-4" />
                    <span>Unban User</span>
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setShowActions(false);
                    setShowEmailModal(true);
                  }}
                  className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                >
                  <Send className="h-4 w-4" />
                  <span>Send Email</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Ban Reason Form */}
        {showBanForm && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-red-800">Ban User</h4>
              <button
                onClick={() => {
                  setShowBanForm(false);
                  setBanReason('');
                }}
                className="p-1 text-red-400 hover:text-red-600 transition-colors duration-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Please provide a detailed reason for banning this user..."
              className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="3"
            />
            <div className="flex space-x-3 mt-3">
              <button
                onClick={handleBan}
                disabled={loading || !banReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                <span>Confirm Ban</span>
              </button>
              <button
                onClick={() => {
                  setShowBanForm(false);
                  setBanReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      <EmailModal
        user={user}
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={onSendEmail}
      />
    </>
  );
};

const StatCard = ({ title, value, icon: Icon, color = 'blue', trend }) => {
  const colorConfigs = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', text: 'text-blue-700', border: 'border-blue-200' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', text: 'text-emerald-700', border: 'border-emerald-200' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', text: 'text-purple-700', border: 'border-purple-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', text: 'text-amber-700', border: 'border-amber-200' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', text: 'text-red-700', border: 'border-red-200' }
  };

  const config = colorConfigs[color] || colorConfigs.blue;

  return (
    <div className={`${config.bg} ${config.border} rounded-xl border p-6 hover:shadow-md transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${config.text}`}>{value}</p>
          {trend && (
            <div className={`flex items-center space-x-1 mt-1 text-xs ${
              trend.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
            }`}>
              <TrendingUp className={`h-3 w-3 ${trend.direction === 'down' ? 'rotate-180' : ''}`} />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${config.bg}`}>
          <Icon className={`h-6 w-6 ${config.icon}`} />
        </div>
      </div>
    </div>
  );
};

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    search: '',
    status: 'all'
  });
  const [statistics, setStatistics] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalTutors: 0,
    bannedUsers: 0,
    pendingTutors: 0
  });
  const [notification, setNotification] = useState(null);

  const showNotification = (type, title, message, duration = 5000) => {
    setNotification({ type, title, message, duration });
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userService.getUsers(filters.type);
      
      if (response.success) {
        setUsers(response.data);
        setStatistics(response.statistics);
      } else {
        setError(response.message);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filters.type]);

  const handleBan = async (userId, reason) => {
    setProcessing(prev => ({ ...prev, [userId]: 'ban' }));
    
    let reasonString;
    
    if (typeof reason === 'string') {
      reasonString = reason;
    } else if (reason && typeof reason === 'object') {
      reasonString = reason.reason || reason.message || JSON.stringify(reason);
    } else {
      reasonString = 'Violation of terms of service';
    }
    
    console.log(`🔄 Banning user ${userId}, reason: ${reasonString}`);
    
    try {
      const response = await userService.banUser(userId, reasonString);
      
      if (response.success) {
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, status: 'banned' }
            : user
        ));
        showNotification(
          'success',
          'User Banned Successfully',
          `${response.data.name} has been banned from the platform.`,
          4000
        );
      } else {
        showNotification(
          'error',
          'Ban Failed',
          response.message || 'Failed to ban user. Please try again.',
          5000
        );
      }
    } catch (err) {
      console.error('Error banning user:', err);
      showNotification(
        'error',
        'Ban Error',
        err.message || 'An unexpected error occurred. Please check the console.',
        5000
      );
    }
    
    setProcessing(prev => ({ ...prev, [userId]: false }));
  };

  const handleUnban = async (userId) => {
    setProcessing(prev => ({ ...prev, [userId]: 'unban' }));
    
    try {
      const response = await userService.unbanUser(userId);
      
      if (response.success) {
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, status: 'active' }
            : user
        ));
        showNotification(
          'success',
          'User Unbanned Successfully',
          `${response.data.name} has been restored and can now access the platform.`,
          4000
        );
      } else {
        showNotification(
          'error',
          'Unban Failed',
          response.message || 'Failed to unban user. Please try again.',
          5000
        );
      }
    } catch (err) {
      console.error('Error unbanning user:', err);
      showNotification(
        'error',
        'Unban Error',
        err.message || 'An unexpected error occurred. Please check the console.',
        5000
      );
    }
    
    setProcessing(prev => ({ ...prev, [userId]: false }));
  };

  // Add this function to handle email sending
  const handleSendEmail = async (userId, emailData) => {
    setProcessing(prev => ({ ...prev, [userId]: 'email' }));
    
    try {
      const response = await userService.sendEmailToUser(userId, emailData);
      
      if (response.success) {
        showNotification(
          'success',
          'Email Sent Successfully',
          response.data.message || 'Email has been sent to the user.',
          4000
        );
      } else {
        showNotification(
          'error',
          'Email Failed',
          response.message || 'Failed to send email. Please try again.',
          5000
        );
      }
    } catch (err) {
      console.error('Error sending email:', err);
      showNotification(
        'error',
        'Email Error',
        err.message || 'An unexpected error occurred. Please check the console.',
        5000
      );
    }
    
    setProcessing(prev => ({ ...prev, [userId]: false }));
  };

  // Filter users based on search and status
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                         user.email.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || 
                         (filters.status === 'banned' && user.status === 'banned') ||
                         (filters.status === 'active' && user.status !== 'banned');
    
    return matchesSearch && matchesStatus;
  });

  // Get theme for user card based on filters
  const getUserTheme = (user) => {
    if (user.status === 'banned') return 'banned';
    if (user.role === 'tutor' && user.verification_status === 'pending') return 'pending';
    if (user.role === 'tutor') return 'tutor';
    if (user.role === 'student') return 'student';
    return 'default';
  };

  const stats = {
    total: statistics.totalUsers || users.length,
    students: statistics.totalStudents || users.filter(u => u.role === 'student').length,
    tutors: statistics.totalTutors || users.filter(u => u.role === 'tutor').length,
    banned: statistics.bannedUsers || users.filter(u => u.status === 'banned').length,
    pendingTutors: statistics.pendingTutors || users.filter(u => u.role === 'tutor' && u.verification_status === 'pending').length
  };

  return (
    <DashboardLayout>
      {/* Notification Popup */}
      {notification && (
        <NotificationPopup
          type={notification.type}
          title={notification.title}
          message={notification.message}
          duration={notification.duration}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-2">Manage students, tutors, and platform access with advanced controls</p>
          </div>
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button
              onClick={fetchUsers}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200 font-medium"
            >
              <Loader className="h-4 w-4" />
              <span>Refresh</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
              <Users className="h-4 w-4" />
              <span>Export Users</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">Unable to load users</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats.total}
          icon={Users}
          color="blue"
          trend={{ direction: 'up', value: '+12%' }}
        />
        <StatCard
          title="Students"
          value={stats.students}
          icon={User}
          color="emerald"
          trend={{ direction: 'up', value: '+8%' }}
        />
        <StatCard
          title="Tutors"
          value={stats.tutors}
          icon={GraduationCap}
          color="purple"
          trend={{ direction: 'up', value: '+15%' }}
        />
        <StatCard
          title="Pending Tutors"
          value={stats.pendingTutors}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Banned Users"
          value={stats.banned}
          icon={Ban}
          color="red"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Users</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              />
            </div>
          </div>

          {/* User Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">User Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <option value="all">All Users</option>
              <option value="student">Students Only</option>
              <option value="tutor">Tutors Only</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="banned">Banned Only</option>
            </select>
          </div>

          {/* Results Count */}
          <div className="flex items-end">
            <div className="bg-gray-50 rounded-lg p-4 w-full">
              <p className="text-sm font-medium text-gray-700">
                Showing <span className="font-bold text-blue-600">{filteredUsers.length}</span> of{' '}
                <span className="font-bold text-gray-900">{users.length}</span> users
              </p>
              {statistics.totalUsers > 0 && statistics.totalUsers !== users.length && (
                <p className="text-xs text-gray-500 mt-1">
                  Total in system: {statistics.totalUsers}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="space-y-6">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Users</h3>
            <p className="text-gray-600">Fetching user data from the server...</p>
          </div>
        ) : filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onBan={handleBan}
              onUnban={handleUnban}
              onSendEmail={handleSendEmail}
              loading={processing[user.id]}
              theme={getUserTheme(user)}
            />
          ))
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600 mb-6">
              {filters.search || filters.type !== 'all' || filters.status !== 'all' 
                ? 'Try adjusting your search criteria or filters to see more results.' 
                : 'No users are currently registered in the system.'}
            </p>
            {(filters.search || filters.type !== 'all' || filters.status !== 'all') && (
              <button
                onClick={() => setFilters({ type: 'all', search: '', status: 'all' })}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UsersList;